import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {UsersInsuranceService} from '../../services/usersinsurance/users.insurance.service';
import {UsersService} from '../../services/users/users.service';
import {MatTabGroup} from '@angular/material/tabs';
import {UiAlertService} from '../../services/ui-alert/ui-alert.service';
import {ToastComponent} from '../toast/toast.component';
import {Subscription} from 'rxjs';
import {Router} from '@angular/router';
import { NavigationStart } from '@angular/router';
import {MessageType} from '../../services/message/message.service';
import {UtilsService} from '../../services/utils/utils.service';

@Component({
  selector: 'app-insurance-beneficiaries',
  templateUrl: './insurance-beneficiaries.component.html',
  styleUrls: ['./insurance-beneficiaries.component.scss'],
})
export class InsuranceBeneficiariesComponent implements OnInit, AfterViewChecked, OnDestroy, AfterViewInit {

  @Input() vendorProduct;
  @Output() formEvent: EventEmitter <any> = new EventEmitter<any>();

  // @ts-ignore
  @ViewChild('tabs') tabGroup: MatTabGroup;

  public beneficiaries = [];
  public addTabName = 'Add Beneficiary';
  private counter = 1;
  public selectedTabIndex = 0;
  public saving = false;
  protected hasPrimary;
  protected currentBeneficiary;
  protected getTel;

  private isActive = false;
  private hasMounted = false;
  private navSub: Subscription;

  constructor(
      private formBuilder: FormBuilder,
      private usersInsuranceService: UsersInsuranceService,
      private usersService: UsersService,
      private uiAlertService: UiAlertService,
      private toastComponent: ToastComponent,
      private router: Router,
      private utilsService: UtilsService
    ) {
  }

  buildForm(beneficiary: any): void {
    beneficiary.form = this.formBuilder.group({
      relationship: new FormControl(beneficiary.relationship, Validators.required),
      beneficiaryType: new FormControl(beneficiary.beneficiaryType, Validators.required),
      firstName: new FormControl(beneficiary.firstName, Validators.required),
      lastName: new FormControl(beneficiary.lastName, Validators.required),
      percentage: new FormControl(beneficiary.percentage),
      ssn: new FormControl(beneficiary.ssn),
      dob: new FormControl(beneficiary.dob),
      email: new FormControl(beneficiary.email),
      phone: new FormControl(beneficiary.phone),
      address1: new FormControl(beneficiary.address1),
      address2: new FormControl(beneficiary.address2),
      city: new FormControl(beneficiary.city),
      state: new FormControl(beneficiary.state),
      postalcode:  new FormControl(beneficiary.postalcode)
    });
    this.getTel = this.utilsService.getPhoneNumberWithDashes(beneficiary.phone || '');
    beneficiary.form.valueChanges.subscribe((value) => {
     this.getFormValidationErrors(beneficiary);
    });
    beneficiary.form.markAllAsTouched();
    beneficiary.initialFormValues = JSON.parse(JSON.stringify(beneficiary.form.value));
    beneficiary.form.get('firstName').valueChanges.subscribe((value: any) => {
      if (value) {
        beneficiary.firstName = value;
      }
      this.beneficiaries = this.beneficiaries.slice();
    });
    beneficiary.form.get('beneficiaryType').valueChanges.subscribe((value: any) => {
      beneficiary.beneficiaryType = value;
    });
    this.formEvent.emit(beneficiary.form);
  }

  ngOnInit(): void {
    // window.addEventListener('beforeunload', this.beforeUnloadListener);
    this.navSub = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        if (!this.hasValidBeneficiaries()) {
          this.toastComponent.presentToast('You must have at least one primary beneficiary.');
          this.router.navigateByUrl(this.router.url); // cancel navigation by routing back to same url
        }
      }
    });
    this.usersInsuranceService.getInsurancePrimary(this.usersService.getCurrentUserId(), this.vendorProductId()).then(result => {
      this.hasPrimary = !!result.id;
    });
    this.usersInsuranceService.usersInsuranceObservable.subscribe(_ => {
      this.hasPrimary = true;
    });
    this.usersInsuranceService.getInsuranceBeneficiaries(this.usersService.getCurrentUserId(), this.vendorProductId()).then(result => {
      this.beneficiaries = result; // .concat({firstName: this.addTabName});
      this.beneficiaries.forEach((beneficiary: any) => this.buildForm(beneficiary));
    });
  }

  ngAfterViewChecked(): void {
    // Determine if this component is visible
    const element = (this as any).elementRef?.nativeElement;
    const isNowActive = element?.offsetParent !== null;

    if (!this.hasMounted) {
      this.hasMounted = true;
      this.isActive = isNowActive;
      if (isNowActive) {
        this.startGuards();
      }
      return;
    }
    if (isNowActive && !this.isActive) {
      this.isActive = true;
      this.startGuards();
    } else if (!isNowActive && this.isActive) {
      this.isActive = false;
      this.stopGuards();
    }
  }

  getFormValidationErrors(beneficiary): void {
    const messages = [];
    Object.keys(beneficiary.form.controls).forEach(key => {
      const controlErrors: ValidationErrors = beneficiary.form.get(key)?.errors;
      if (controlErrors) {
        Object.keys(controlErrors).forEach(keyError => {
          let message = '';
          switch (keyError) {
            case 'required':
              message = `${key} is required`;
              break;
            case 'min':
              message = `${key} must be at least ${controlErrors[keyError].min}`;
              break;
            case 'max':
              message = `${key} must be at most ${controlErrors[keyError].max}`;
              break;
            default:
              // message = `${key} is invalid (${keyError})`;
          }
          if (message) {
            messages.push(message);
          }
        });
      }
    });
    beneficiary.errorMessages = messages.join(', ');
  }


  startGuards(): void {
    if (!this.navSub) {
      this.navSub = this.router.events.subscribe(event => {
        if (event instanceof NavigationStart) {
          if (!this.hasValidBeneficiaries()) {
            this.toastComponent.presentToast('You must have at least one primary beneficiary.');
            this.router.navigateByUrl(this.router.url); // cancel navigation by routing back to same url
          }
        }
      });
    }
  }

  stopGuards(): void {
    if (this.navSub) {
      this.navSub.unsubscribe();
      this.navSub = null;
    }
  }

  ngOnDestroy(): void {
    this.stopGuards();
  }

  hasValidBeneficiaries(): boolean {
    if (this.beneficiaries.length === 0) {
      return true; // nothing to validate
    }
    const atLeastOneFormDirty = this.beneficiaries.map(beneficiary => beneficiary.form.dirty).reduce((accumulator, currentValue) => accumulator || currentValue, 0);
    if (atLeastOneFormDirty) {
      return true;  // let dirty form guard catch it
    }
    const primaryCount = this.beneficiaries.filter(b => b.form?.get('beneficiaryType')?.value === 'P').length;
    return primaryCount > 0;
  }

  ngAfterViewInit(): void {
    this.setTabClass();
    this.tabGroup.selectedIndex = 0;
  }

  onTabChanged(event): void {
    this.setTabClass();
    this.currentBeneficiary = this.beneficiaries[this.tabGroup.selectedIndex];
  }

  setVal(beneficiary: any, value: any, frmControlName: string) {
    beneficiary.form.get(frmControlName).setValue(value);
    beneficiary.form.get(frmControlName).markAsDirty();
  }

  setTabClass(): void {
    const elements = document.querySelectorAll('.mat-tab-label');
    elements.forEach(function(element) {
      if (element instanceof HTMLElement) {
        element.style.opacity = '1';
      }
    });
  }

  addBeneficiary(): void {
    this.beneficiaries.unshift({firstName: ``});
    this.buildForm(this.beneficiaries[0]);
    this.tabGroup.selectedIndex = 1;
    this.currentBeneficiary = this.beneficiaries[0];
    setTimeout(() => {
      this.tabGroup.selectedIndex = 0;
    }, 50);
  }

  resetClicked(beneficiary: any): void {
    beneficiary.form.reset();
    // tslint:disable-next-line:forin
    for (const field in beneficiary.initialFormValues) {
      beneficiary.form.get(field).setValue(beneficiary.initialFormValues[field]);
    }
  }

  private testTotalPercentage(): string {
    let totalPrimaryPercentage = 0;
    let totalContingentPercentage = 0;
    let hasPrimary = false;
    let hasContingent = false;
    this.beneficiaries.forEach(beneficiary => {
      function toNumber(input) {
        const num = Number(input);
        return isNaN(num) ? null : num;
      }
      console.log('TYPE', beneficiary.beneficiaryType, beneficiary.firstName)
      if (beneficiary.beneficiaryType === 'P') {
        totalPrimaryPercentage += toNumber(beneficiary.form.get('percentage').value);
        hasPrimary = true;
      } else {
        totalContingentPercentage += toNumber(beneficiary.form.get('percentage').value);
        hasContingent = true;
      }
    });
    if (hasPrimary && totalPrimaryPercentage !== 100) {
      return `Percentages for all primary beneficiaries must add up to 100%, but you have ${totalPrimaryPercentage}. You may add one or more additional beneficiaries before saving this one.`;
    }
    if (hasContingent && totalContingentPercentage !== 100) {
      return `Percentages for all contingent beneficiaries must add up to 100%, but you have ${totalContingentPercentage}. You may add one or more additional beneficiaries before saving this one.`;
    }
    return '';
  }

  saveClicked(beneficiary: any): void {
    const errorMessage = this.testTotalPercentage();
    if (errorMessage) {
      this.toastComponent.presentToast(errorMessage);
      return;
    }
    const newInfo = {
      relationship: beneficiary.form.get('relationship')?.value,
      type: beneficiary.form.get('beneficiaryType')?.value,
      percentage: beneficiary.form.get('percentage')?.value,
      firstName: beneficiary.form.get('firstName')?.value,
      lastName: beneficiary.form.get('lastName')?.value,
      ssn: beneficiary.form.get('ssn')?.value,
      dob: this.utilsService.convertDate(beneficiary.form.get('dob')?.value),
      email: beneficiary.form.get('email')?.value,
      phone: beneficiary.form.get('phone')?.value,
      address1: beneficiary.form.get('address1')?.value,
      address2: beneficiary.form.get('address2')?.value,
      city: beneficiary.form.get('city')?.value,
      state: beneficiary.form.get('state')?.value,
      postalcode:  beneficiary.form.get('postalcode')?.value
    };
    this.saving = true;
    if (beneficiary.id && !this.beneficiaries[0].fromWallit) {
      this.usersInsuranceService.modifyInsuranceBeneficiary(this.usersService.getCurrentUserId(), beneficiary.id, newInfo).then(_ => {
        this.saving = false;
        this.usersInsuranceService.updateCarriers(this.usersService.getCurrentUserId());
        beneficiary.form.markAsPristine();
        this.toastComponent.presentToast('User insurance beneficiary info updated');
      });
    } else {
      this.usersInsuranceService.createInsuranceBeneficiary(
          this.usersService.getCurrentUserId(),
          this.vendorProductId(),
          beneficiary.form.get('firstName')?.value,
          beneficiary.form.get('lastName')?.value,
          beneficiary.form.get('ssn')?.value,
          beneficiary.form.get('relationship')?.value,
          beneficiary.form.get('beneficiaryType')?.value,
          beneficiary.form.get('percentage')?.value,
          this.utilsService.convertDate(beneficiary.form.get('dob')?.value),
          beneficiary.form.get('email')?.value,
          beneficiary.form.get('phone')?.value,
          beneficiary.form.get('address1')?.value,
          beneficiary.form.get('address2')?.value,
          beneficiary.form.get('city')?.value,
          beneficiary.form.get('state')?.value,
          beneficiary.form.get('postalcode')?.value
      ).then(result => {
        this.saving = false;
        beneficiary = Object.assign({}, beneficiary, newInfo);
        this.currentBeneficiary.id = result.id;
        this.toastComponent.presentToast('User insurance beneficiary info created');
        this.beneficiaries = this.beneficiaries.slice();
        beneficiary.form.markAsPristine();
      });
    }
  }

  removeClicked(beneficiary: any): void {
    if (beneficiary.id) {
      this.uiAlertService.presentAlertConfirm('Do you really want to remove this beneficiary?').then(ok => {
        if (ok) {
          this.usersInsuranceService.deleteInsuranceBeneficiary(this.usersService.getCurrentUserId(), beneficiary.id).then(_ => {
            this.usersInsuranceService.updateCarriers(this.usersService.getCurrentUserId());
            this.toastComponent.presentToast('beneficiary removed');
            this.beneficiaries.splice(this.beneficiaries.findIndex((item: { id: string; }) => item.id === beneficiary.id), 1);
            this.beneficiaries = this.beneficiaries.slice();
          });
        }
      });
    } else {
      this.toastComponent.presentToast('beneficiary removed');
      this.beneficiaries.splice(this.tabGroup.selectedIndex, 1);
      this.beneficiaries = this.beneficiaries.slice();
    }
  }

  vendorProductId() {
    // return this.vendorProduct?.id || 'wallit';
    return 'wallit';
  }

  showError(form, fieldName: string, errorMessage: string): string | undefined {
    return (form.get(fieldName).errors && form.get(fieldName).touched) ? errorMessage : null;
  }

  enforceRange(form, event: any): void {
    const input = event.target as HTMLInputElement;
    const value = parseInt(input.value, 10);

    if (value < 1) {
      input.value = '1';
      form.get('amount')?.setValue(1);
    } else if (value > 100) {
      input.value = '100';
      form.get('amount')?.setValue(100);
    }
  }

}
